// src/contexts/PanelContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';

// Panel type constants
export const PanelType = {
  CREATOR: 'creator',
  CUSTOMER: 'customer',
  STUDENT: 'student',
  TRADING: 'trading',
};

// Roles that can access each panel
const CREATOR_PANEL_ROLES = ['admin', 'employee'];
const CUSTOMER_PANEL_ROLES = ['admin', 'employee', 'customer'];
const STUDENT_PANEL_ROLES = ['admin', 'employee', 'student'];
const CUSTOMER_ONLY_ROLES = ['customer'];
const TRADING_PANEL_ROLES = ['admin', 'employee'];
const STUDENT_ONLY_ROLES = ['student'];

const PanelContext = createContext();

export const usePanel = () => {
  return useContext(PanelContext);
};

export const PanelProvider = ({ children }) => {
  const location = useLocation();
  const [activePanel, setActivePanel] = useState(null);
  const [customerModules, setCustomerModules] = useState([]);
  // Ref to track last synced pathname - prevents URL sync effect from
  // reverting activePanel when switchPanel fires before navigate completes
  const lastSyncedPath = useRef(null);
  const [panelAccess, setPanelAccess] = useState({
    creator: false,
    customer: false,
    student: false,
    trading: false,
    default: 'creator'
  });
  const [isLoading, setIsLoading] = useState(true);

  // Get user role from localStorage
  const getUserRole = useCallback(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      return userData?.active_team?.user_role_in_team?.toLowerCase() || null;
    }
    return null;
  }, []);

  // Determine panel access based on user role and account type
  const determineAccess = useCallback(() => {
    const role = getUserRole();
    if (!role) {
      return { creator: false, customer: false, student: false, trading: false, default: 'creator' };
    }

    const userStr = localStorage.getItem('user');
    const userData = userStr ? JSON.parse(userStr) : {};
    const teamAccountType = userData?.active_team?.team_account_type || userData?.account_type || 'individual';
    const isBusinessAccount = teamAccountType === 'business';

    const canAccessCreator = CREATOR_PANEL_ROLES.includes(role);
    // Customer panel and Student portal require business account
    const canAccessCustomer = isBusinessAccount && CUSTOMER_PANEL_ROLES.includes(role);
    const canAccessStudent = isBusinessAccount && STUDENT_PANEL_ROLES.includes(role);
    const canAccessTrading = isBusinessAccount && TRADING_PANEL_ROLES.includes(role);


    let defaultPanel = 'creator';
    if (STUDENT_ONLY_ROLES.includes(role)) {
      defaultPanel = 'student';
    } else if (CUSTOMER_ONLY_ROLES.includes(role)) {
      defaultPanel = 'customer';
    }

    return {
      creator: canAccessCreator,
      customer: canAccessCustomer,
      student: canAccessStudent,
      trading: canAccessTrading,
      default: defaultPanel
    };
  }, [getUserRole]);

  // Initialize panel state
  useEffect(() => {
    const initializePanel = () => {
      const access = determineAccess();
      setPanelAccess(access);

      // Check localStorage for saved panel preference
      const savedPanel = localStorage.getItem('active_panel');

      if (savedPanel && (
        (savedPanel === 'creator' && access.creator) ||
        (savedPanel === 'customer' && access.customer) ||
        (savedPanel === 'student' && access.student) ||
        (savedPanel === 'trading' && access.trading)
      )) {
        setActivePanel(savedPanel);
      } else {
        // Use default panel based on role
        setActivePanel(access.default);
        localStorage.setItem('active_panel', access.default);
      }

      setIsLoading(false);
    };

    initializePanel();
  }, [determineAccess]);

  // Re-check panel access on route changes (handles account type conversion)
  useEffect(() => {
    if (isLoading) return;

    const newAccess = determineAccess();
    // Only update if access actually changed
    if (newAccess.creator !== panelAccess.creator || newAccess.customer !== panelAccess.customer || newAccess.student !== panelAccess.student || newAccess.trading !== panelAccess.trading) {
      setPanelAccess(newAccess);

      // If current panel is no longer accessible, switch to default
      if (
        (activePanel === 'creator' && !newAccess.creator) ||
        (activePanel === 'customer' && !newAccess.customer) ||
        (activePanel === 'student' && !newAccess.student) ||
        (activePanel === 'trading' && !newAccess.trading)
      ) {
        setActivePanel(newAccess.default);
        localStorage.setItem('active_panel', newAccess.default);
      }
    }
  }, [location.pathname, isLoading, determineAccess, panelAccess, activePanel]);

  // Sync panel state with URL path - only when URL actually changes
  useEffect(() => {
    if (isLoading) return;

    const path = location.pathname;

    // Only run sync logic when the URL path actually changes, not when
    // activePanel changes (avoids race condition with switchPanel + navigate)
    if (lastSyncedPath.current !== null && path === lastSyncedPath.current) return;
    lastSyncedPath.current = path;

    if (path.startsWith('/trading-portal') && activePanel !== PanelType.TRADING && panelAccess.trading) {
      setActivePanel(PanelType.TRADING);
      localStorage.setItem('active_panel', PanelType.TRADING);
    } else if (path.startsWith('/student-portal') && activePanel !== PanelType.STUDENT && panelAccess.student) {
      setActivePanel(PanelType.STUDENT);
      localStorage.setItem('active_panel', PanelType.STUDENT);
    } else if (path.startsWith('/customer-panel') && activePanel !== PanelType.CUSTOMER && panelAccess.customer) {
      setActivePanel(PanelType.CUSTOMER);
      localStorage.setItem('active_panel', PanelType.CUSTOMER);
    } else if ((path.startsWith('/chatbot/') || path === '/chatbots') && activePanel !== PanelType.CREATOR && panelAccess.creator) {
      setActivePanel(PanelType.CREATOR);
      localStorage.setItem('active_panel', PanelType.CREATOR);
    }
  }, [location.pathname, isLoading, activePanel, panelAccess]);

  // Switch panel function
  const switchPanel = useCallback((panel) => {
    if (panel === PanelType.CREATOR && !panelAccess.creator) {
      console.warn('User does not have access to Creator Panel');
      return false;
    }
    if (panel === PanelType.CUSTOMER && !panelAccess.customer) {
      console.warn('User does not have access to Customer Panel');
      return false;
    }
    if (panel === PanelType.STUDENT && !panelAccess.student) {
      console.warn('User does not have access to Student Portal');
      return false;
    }
    if (panel === PanelType.TRADING && !panelAccess.trading) {
      console.warn('User does not have access to Trading Portal');
      return false;
    }

    setActivePanel(panel);
    localStorage.setItem('active_panel', panel);
    return true;
  }, [panelAccess]);

  // Check if user can access Creator Panel
  const canAccessCreatorPanel = useCallback(() => {
    return panelAccess.creator;
  }, [panelAccess]);

  // Check if user can access Customer Panel
  const canAccessCustomerPanel = useCallback(() => {
    return panelAccess.customer;
  }, [panelAccess]);

  // Check if user can access Student Portal
  const canAccessStudentPanel = useCallback(() => {
    return panelAccess.student;
  }, [panelAccess]);

  // Check if user can access Trading Portal
  const canAccessTradingPanel = useCallback(() => {
    return panelAccess.trading;
  }, [panelAccess]);

  // Check if user can switch between panels (has access to more than one)
  const canSwitchPanels = useCallback(() => {
    const accessCount = [panelAccess.creator, panelAccess.customer, panelAccess.student, panelAccess.trading].filter(Boolean).length;
    return accessCount > 1;
  }, [panelAccess]);

  // Fetch customer panel modules
  const fetchCustomerModules = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/modules?panel=customer');
      if (response.data.success) {
        const data = JSON.parse(response.data.data);
        setCustomerModules(data.modules || []);
        return data.modules;
      }
    } catch (error) {
      console.error('Error fetching customer modules:', error);
    }
    return [];
  }, []);

  // Fetch student portal modules
  const fetchStudentModules = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/modules?panel=student');
      if (response.data.success) {
        const data = JSON.parse(response.data.data);
        return data.modules;
      }
    } catch (error) {
      console.error('Error fetching student modules:', error);
    }
    return [];
  }, []);

  // Fetch trading portal modules
  const fetchTradingModules = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/modules?panel=trading');
      if (response.data.success) {
        const data = JSON.parse(response.data.data);
        return data.modules;
      }
    } catch (error) {
      console.error('Error fetching trading modules:', error);
    }
    return [];
  }, []);

  // Refresh panel access (useful after login)
  const refreshPanelAccess = useCallback(() => {
    const access = determineAccess();
    setPanelAccess(access);

    // If current panel is no longer accessible, switch to default
    if (
      (activePanel === 'creator' && !access.creator) ||
      (activePanel === 'customer' && !access.customer) ||
      (activePanel === 'student' && !access.student) ||
      (activePanel === 'trading' && !access.trading)
    ) {
      setActivePanel(access.default);
      localStorage.setItem('active_panel', access.default);
    }
  }, [determineAccess, activePanel]);

  // Get the other panel (for switching)
  const getOtherPanel = useCallback(() => {
    return activePanel === PanelType.CREATOR ? PanelType.CUSTOMER : PanelType.CREATOR;
  }, [activePanel]);

  const value = {
    activePanel,
    setActivePanel,
    switchPanel,
    panelAccess,
    customerModules,
    setCustomerModules,
    fetchCustomerModules,
    fetchStudentModules,
    fetchTradingModules,
    canAccessCreatorPanel,
    canAccessCustomerPanel,
    canAccessStudentPanel,
    canAccessTradingPanel,
    canSwitchPanels,
    refreshPanelAccess,
    getOtherPanel,
    getUserRole,
    isLoading,
    PanelType,
  };

  return (
    <PanelContext.Provider value={value}>
      {children}
    </PanelContext.Provider>
  );
};

export default PanelContext;
